import { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../hooks/useAuth';
import { logsApi, LogEntry, LogsParams } from '../lib/logs';
import { Navigate } from 'react-router-dom';
import { Filter, ArrowUpDown } from 'lucide-react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';

type SortField = 'timestamp' | 'level' | 'service';
type SortDirection = 'asc' | 'desc';

const LEVEL_OPTIONS = ['error', 'warn', 'info', 'debug'] as const;

/** Stable service names from `createServiceLogger` in the backend. */
const SERVICE_OPTIONS = [
  'app',
  'http',
  'logs',
  'audit',
  'user',
  'email',
  'site-controller',
  'pdeg-listener',
  'pdeg-polling',
  'pdeg-client',
  'area',
  'area-preset-states',
  'usage',
  'manager-group',
  'db-seed',
  'db-reset',
  'db-timescale',
] as const;

function CheckboxFilterGroup({
  id,
  label,
  options,
  selected,
  onChange,
  formatLabel,
}: {
  id: string;
  label: string;
  options: readonly string[];
  selected: string[];
  onChange: (next: string[]) => void;
  formatLabel?: (value: string) => string;
}) {
  const selectedSet = new Set(selected);

  const toggle = (value: string, checked: boolean) => {
    const next = checked
      ? [...selected, value]
      : selected.filter((v) => v !== value);
    onChange(next);
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between gap-2">
        <Label id={`${id}-label`}>{label}</Label>
        <div className="flex gap-1">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-7 px-2 text-xs"
            onClick={() => onChange([...options])}
          >
            All
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-7 px-2 text-xs"
            onClick={() => onChange([])}
          >
            None
          </Button>
        </div>
      </div>
      <div
        role="group"
        aria-labelledby={`${id}-label`}
        className="rounded-md border border-input bg-background p-2 max-h-40 overflow-y-auto space-y-1"
      >
        {options.map((option) => {
          const checkboxId = `${id}-${option}`;
          return (
            <label
              key={option}
              htmlFor={checkboxId}
              className="flex items-center gap-2 rounded px-2 py-1 text-sm hover:bg-accent cursor-pointer"
            >
              <Checkbox
                id={checkboxId}
                checked={selectedSet.has(option)}
                onCheckedChange={(checked) => toggle(option, checked === true)}
              />
              <span className="font-mono text-xs">
                {formatLabel ? formatLabel(option) : option}
              </span>
            </label>
          );
        })}
      </div>
    </div>
  );
}

export default function LogsPage() {
  const { user, loading: authLoading } = useAuth();
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState<LogsParams>({
    limit: 100,
    levels: ['error', 'warn'],
    services: [...SERVICE_OPTIONS],
  });
  const [sortField, setSortField] = useState<SortField>('timestamp');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');

  const isSuperUser = user?.role === 'super_user';
  const hasLevelSelection = (filters.levels?.length ?? 0) > 0;
  const hasServiceSelection = (filters.services?.length ?? 0) > 0;
  const filtersAllowResults = hasLevelSelection && hasServiceSelection;

  const { data, isLoading, error } = useQuery({
    queryKey: ['logs', page, filters],
    queryFn: async () => {
      // Empty checkbox selection means match nothing (not "no filter").
      if (!filtersAllowResults) {
        return {
          logs: [],
          pagination: {
            page,
            limit: filters.limit ?? 100,
            total: 0,
            totalPages: 0,
          },
        };
      }
      return logsApi.getAll({
        ...filters,
        page,
      });
    },
    enabled: isSuperUser,
  });

  const sortedLogs = useMemo(() => {
    if (!data?.logs) return [];
    const items = [...data.logs];
    items.sort((a: LogEntry, b: LogEntry) => {
      let aVal: string | null = null;
      let bVal: string | null = null;

      if (sortField === 'timestamp') {
        aVal = a.timestamp ?? a.createdAt;
        bVal = b.timestamp ?? b.createdAt;
      } else if (sortField === 'level') {
        aVal = a.level;
        bVal = b.level;
      } else if (sortField === 'service') {
        aVal = a.service ?? '';
        bVal = b.service ?? '';
      }

      if (!aVal && !bVal) return 0;
      if (!aVal) return sortDirection === 'asc' ? -1 : 1;
      if (!bVal) return sortDirection === 'asc' ? 1 : -1;

      if (sortField === 'timestamp') {
        const aTime = new Date(aVal).getTime();
        const bTime = new Date(bVal).getTime();
        return sortDirection === 'asc' ? aTime - bTime : bTime - aTime;
      }

      const cmp = aVal.localeCompare(bVal);
      return sortDirection === 'asc' ? cmp : -cmp;
    });
    return items;
  }, [data?.logs, sortField, sortDirection]);

  if (authLoading) {
    return (
      <div className="px-2 py-3 sm:px-0">
        <div className="text-center py-12">
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  if (!isSuperUser) {
    return <Navigate to="/" replace />;
  }

  const formatDateTime = (value?: string | null) => {
    if (!value) return '—';
    return new Date(value).toLocaleString();
  };

  const toggleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  const getLevelVariant = (level: string): 'default' | 'secondary' | 'outline' | 'destructive' => {
    const normalized = level.toLowerCase();
    if (normalized === 'error') return 'destructive';
    if (normalized === 'warn' || normalized === 'warning') return 'outline';
    if (normalized === 'info') return 'default';
    return 'secondary';
  };

  return (
    <div className="py-3">
      <div className="mb-6">
        <h1 className="text-2xl sm:text-3xl font-bold text-foreground font-heading">Application Logs</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          View application log messages stored in the database
        </p>
      </div>

      {/* Filters */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Filter className="h-5 w-5" />
            Filters
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="space-y-2">
              <Label htmlFor="logs-filter-start-date">Start Date</Label>
              <Input
                type="datetime-local"
                id="logs-filter-start-date"
                value={filters.startDate || ''}
                onChange={(e) =>
                  setFilters({ ...filters, startDate: e.target.value || undefined })
                }
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="logs-filter-end-date">End Date</Label>
              <Input
                type="datetime-local"
                id="logs-filter-end-date"
                value={filters.endDate || ''}
                onChange={(e) =>
                  setFilters({ ...filters, endDate: e.target.value || undefined })
                }
              />
            </div>
            <CheckboxFilterGroup
              id="logs-filter-levels"
              label="Levels"
              options={LEVEL_OPTIONS}
              selected={filters.levels ?? []}
              onChange={(levels) => setFilters((prev) => ({ ...prev, levels }))}
              formatLabel={(value) => value.toUpperCase()}
            />
            <CheckboxFilterGroup
              id="logs-filter-services"
              label="Services"
              options={SERVICE_OPTIONS}
              selected={filters.services ?? []}
              onChange={(services) => setFilters((prev) => ({ ...prev, services }))}
            />
            <div className="space-y-2 sm:col-span-2 lg:col-span-4">
              <Label htmlFor="logs-filter-search">Search message</Label>
              <Input
                type="text"
                id="logs-filter-search"
                placeholder="Search in message..."
                value={filters.search || ''}
                onChange={(e) =>
                  setFilters({ ...filters, search: e.target.value || undefined })
                }
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      {isLoading ? (
        <Card className="text-center py-12">
          <p className="text-muted-foreground">Loading logs...</p>
        </Card>
      ) : error ? (
        <Card className="text-center py-12">
          <p className="text-destructive">Error loading logs</p>
        </Card>
      ) : !data || data.logs.length === 0 ? (
        <Card className="text-center py-12">
          <p className="text-muted-foreground">No logs found</p>
        </Card>
      ) : (
        <>
          <Card>
            <Table className="table-fixed">
              <TableHeader>
                <TableRow>
                  <TableHead
                    className="w-[14%] px-3 sm:px-4 md:px-6 cursor-pointer select-none"
                    onClick={() => toggleSort('timestamp')}
                  >
                    <span className="inline-flex items-center gap-1">
                      Timestamp
                      <ArrowUpDown className="h-3 w-3" />
                    </span>
                  </TableHead>
                  <TableHead
                    className="w-[8%] px-3 sm:px-4 md:px-6 cursor-pointer select-none"
                    onClick={() => toggleSort('level')}
                  >
                    <span className="inline-flex items-center gap-1">
                      Level
                      <ArrowUpDown className="h-3 w-3" />
                    </span>
                  </TableHead>
                  <TableHead
                    className="w-[12%] px-3 sm:px-4 md:px-6 cursor-pointer select-none"
                    onClick={() => toggleSort('service')}
                  >
                    <span className="inline-flex items-center gap-1">
                      Service
                      <ArrowUpDown className="h-3 w-3" />
                    </span>
                  </TableHead>
                  <TableHead className="w-[36%] px-3 sm:px-4 md:px-6">
                    Message
                  </TableHead>
                  <TableHead className="w-[30%] px-3 sm:px-4 md:px-6">
                    Context
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sortedLogs.map((log) => (
                  <TableRow key={log.id}>
                    <TableCell className="px-3 sm:px-4 md:px-6 align-top">
                      <div className="truncate" title={formatDateTime(log.timestamp ?? log.createdAt)}>
                        {formatDateTime(log.timestamp ?? log.createdAt)}
                      </div>
                    </TableCell>
                    <TableCell className="px-3 sm:px-4 md:px-6 align-top">
                      <Badge variant={getLevelVariant(log.level)}>{log.level}</Badge>
                    </TableCell>
                    <TableCell className="px-3 sm:px-4 md:px-6 align-top">
                      <div className="truncate" title={log.service || undefined}>
                        {log.service || '—'}
                      </div>
                    </TableCell>
                    <TableCell className="px-3 sm:px-4 md:px-6 align-top">
                      <div className="truncate" title={log.message}>
                        {log.message}
                      </div>
                    </TableCell>
                    <TableCell className="px-3 sm:px-4 md:px-6 align-top">
                      <div className="font-mono text-xs whitespace-pre-wrap break-words overflow-hidden">
                        {log.context ?? '—'}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
          {data.pagination && data.pagination.totalPages > 1 && (
            <div className="mt-4 flex flex-col sm:flex-row items-center justify-between gap-4">
              <div className="text-sm text-muted-foreground text-center sm:text-left">
                Showing page {data.pagination.page} of {data.pagination.totalPages} (
                {data.pagination.total} total logs)
              </div>
              <div className="flex space-x-2">
                <Button
                  variant="outline"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                >
                  Previous
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setPage((p) => p + 1)}
                  disabled={page >= data.pagination.totalPages}
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
